<template>
    <div>
        <div v-if="Permission.INVITE_USERS" class="mb-3">
            <div>
                <Link as="button" :href="$route('users.invitations.create')"
                      class="btn btn-bg-white btn-text-dark-50 btn-icon-primary btn-hover-primary font-weight-bolder font-size-sm px-5 my-1">
                    <span class="svg-icon svg-icon-md">
                        <i class="fas fa-user-plus mr-1"/>
                    </span>
                    {{ __('navigation.invite_user') }}
                </Link>
            </div>
        </div>

        <div class="card">
            <div class="card-header">
                <div class="card-title m-0">
                    <h3 class="card-label">
                        {{ __('navigation.invitations') }}
                    </h3>
                    <p class="m-0">
                        View existing user invites.
                    </p>
                </div>
            </div>

            <div class="card-body">
                <div class="table-responsive">
                    <table class="table table-hover table-vertical-center table-head-bg">
                        <thead>
                            <tr>
                                <th>
                                    <TableSort field="user" :value="sort" @input="onSortChange">
                                        {{ __('general.user') }}
                                    </TableSort>
                                </th>
                                <th class="text-center user-select-none">
                                    <TableSort field="agency" :value="sort" @input="onSortChange">
                                        {{ __('agency.agency') }}
                                    </TableSort>
                                </th>
                                <th class="text-center">
                                    <TableSort field="stores" :value="sort" @input="onSortChange">
                                        {{ __('general.stores') }}
                                    </TableSort>
                                </th>
                                <th class="text-center user-select-none">
                                    <TableSort field="created_at" :value="sort" @input="onSortChange">
                                        {{ __('user.invited_at') }}
                                    </TableSort>
                                </th>
                                <th class="text-center">
                                    <TableSort field="accepted_at" :value="sort" @input="onSortChange">
                                        {{ __('user-request.status') }}
                                    </TableSort>
                                </th>
                                <th class="text-center user-select-none">{{ __('general.actions') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="invitation in invitations.data" :key="invitation.id">
                                <td>
                                    <user v-if="invitation.user"
                                          :user="invitation.user">
                                    </user>
                                    <div v-else
                                         class="d-flex flex-column justify-content-center h-3em pl-1">
                                        <div style="opacity: 0.85;">
                                            {{ invitation.email }}
                                        </div>
                                        <div class="text-sm text-muted">
                                            {{ invitation.phone || 'Keine Telefonnummer verfügbar' }}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <!-- Agency -->
                                    <agency :agency="invitation.agency"/>
                                </td>
                                <td>
                                    <!-- Store Count -->
                                    <big-number :value="invitation.stores_count"/>
                                </td>
                                <td>
                                    <!-- Dates -->
                                    <date-time :timestamp="invitation.created_at"/>
                                </td>
                                <td>
                                    <!-- Status -->
                                    <div class="d-flex h-3em align-items-center justify-content-center user-select-none">
                                        <span v-if="invitation.accepted_at" class="font-size-lg">
                                            <i class="fas fa-check-circle text-success"></i>&nbsp;{{ __('user-request.approved') }}
                                        </span>
                                        <span v-else class="text-muted">{{ __('user-request.pending') }}</span>
                                    </div>
                                </td>
                                <td>
                                    <!-- Actions -->
                                    <actions class="user-select-none">
                                        <div class="btn-group">
                                            <Link v-if="Permission.EDIT_USER_INVITATIONS"
                                                  as="button"
                                                  :href="$route('users.invitations.edit', {'invitation': invitation.id})"
                                                  class="btn btn-default btn-hover-primary"
                                                  title="Edit">
                                                <i class="fas fa-edit"/>
                                            </Link>
                                            <Link :href="$route('users.invitations.show', {'invitation': invitation.id})"
                                                  as="button"
                                                  class="btn btn-default btn-hover-primary"
                                                  title="View">
                                                <i class="fas fa-eye"/>
                                            </Link>
                                        </div>
                                    </actions>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colspan="6">
                                    <pagination :loading="loading"
                                                :pagination="invitations"
                                                v-on:changePage="pageChanged"
                                                v-on:changePerPage="perPageChanged"
                                                class="user-select-none mt-2">
                                    </pagination>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DateTime from '@components/TableComponents/DateTime'
import Actions from '@components/TableComponents/Actions'
import ActionButton from '@components/TableComponents/ActionButton'
import Agency from '@components/TableComponents/Agency'
import User from '@components/TableComponents/User'
import BigNumber from '@components/TableComponents/BigNumber'
import Pagination from '@components/TableComponents/Pagination'
import {PermissionsMixin} from '@js/permissions'
import { Link } from '@inertiajs/vue2';
import TableSort from '@components/Table/TableSort.vue'
import {rewriteUrlNew} from '@composables/rewriteUrlNew'

export default {
    components: {
        TableSort,
        DateTime,
        Actions,
        ActionButton,
        Agency,
        User,
        Pagination,
        BigNumber,
        Link
    },

    mixins: [PermissionsMixin],

    props: {
        sort: Object,
        invitations: Object //paginated
    },

    data() {
        return {
            loading: false,
        }
    },

    methods: {
        onSortChange(params) {
            if (JSON.stringify(this.sort) !== JSON.stringify(params)) {
                rewriteUrlNew('sort', params)
                this.refreshTableContents();
            }
        },

        pageChanged(n) {
            rewriteUrlNew('page', n);
            this.refreshTableContents();
        },

        perPageChanged(n) {
            rewriteUrlNew('per_page', n);
            this.refreshTableContents();
        },

        refreshTableContents() {
            this.loading = true;
            this.$inertia.visit(window.location.href, {
                    only: ['invitations'],
                    onFinish() {
                        this.loading = false;
                    }
                },
            )
        },
    },
}
</script>
